import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';

type Props = NativeStackScreenProps<MainStackParamList, 'JoinRoutine'>;

interface RoutineData {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  creatorName?: string;
  daysPerWeek: number;
  shared: boolean;
}

const JoinRoutineScreen = ({ navigation }: Props) => {
  const [routineCode, setRoutineCode] = useState('');
  const [username, setUsername] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [foundRoutine, setFoundRoutine] = useState<RoutineData | null>(null);

  const searchByCode = async () => {
    if (!routineCode.trim()) {
      Alert.alert('Error', 'Por favor ingresa un código de rutina');
      return;
    }

    try {
      setSearchLoading(true);
      setFoundRoutine(null);

      const routinesRef = collection(firestore, 'routines');
      const q = query(routinesRef, where('routineCode', '==', routineCode.trim().toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        Alert.alert('Error', 'No se encontró ninguna rutina con este código');
        return;
      }

      // Get the first match
      const routineDoc = querySnapshot.docs[0];
      const routineData = routineDoc.data();

      // Check if the routine is shared
      if (!routineData.shared) {
        Alert.alert('Error', 'Esta rutina no está compartida');
        return;
      }

      // Get creator name
      const creatorDoc = await getDoc(doc(firestore, 'users', routineData.creatorId));
      let creatorName = 'Usuario desconocido';
      
      if (creatorDoc.exists()) {
        const creatorData = creatorDoc.data();
        creatorName = `${creatorData.firstName} ${creatorData.lastName}`;
      }

      setFoundRoutine({
        id: routineDoc.id,
        name: routineData.name,
        description: routineData.description,
        creatorId: routineData.creatorId,
        creatorName,
        daysPerWeek: routineData.daysPerWeek,
        shared: routineData.shared
      });
    } catch (error) {
      console.error('Error al buscar rutina:', error);
      Alert.alert('Error', 'No se pudo buscar la rutina');
    } finally {
      setSearchLoading(false);
    }
  };

  const searchByUsername = async () => {
    if (!username.trim()) {
      Alert.alert('Error', 'Por favor ingresa un nombre de usuario');
      return;
    }

    try {
      setSearchLoading(true);
      setFoundRoutine(null);

      // First find the user
      const usersRef = collection(firestore, 'users');
      const userQuery = query(usersRef, where('username', '==', username.trim()));
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        Alert.alert('Error', 'No se encontró ningún usuario con este nombre');
        return;
      }

      const userData = userSnapshot.docs[0].data();
      const userId = userSnapshot.docs[0].id;

      // Now find shared routines from this user
      const routinesRef = collection(firestore, 'routines');
      const routinesQuery = query(
        routinesRef, 
        where('creatorId', '==', userId),
        where('shared', '==', true)
      );
      
      const routinesSnapshot = await getDocs(routinesQuery);

      if (routinesSnapshot.empty) {
        Alert.alert('Error', 'Este usuario no tiene rutinas compartidas');
        return;
      }

      // Just take the first shared routine for simplicity
      const routineDoc = routinesSnapshot.docs[0];
      const routineData = routineDoc.data();

      setFoundRoutine({
        id: routineDoc.id,
        name: routineData.name,
        description: routineData.description,
        creatorId: userId,
        creatorName: `${userData.firstName} ${userData.lastName}`,
        daysPerWeek: routineData.daysPerWeek,
        shared: routineData.shared
      });
    } catch (error) {
      console.error('Error al buscar usuario:', error);
      Alert.alert('Error', 'No se pudo buscar el usuario');
    } finally {
      setSearchLoading(false);
    }
  };

  const joinRoutine = async () => {
    if (!foundRoutine) return;

    try {
      setJoinLoading(true);

      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      // Get the user document
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }

      // Get the routine document again to ensure it's up to date
      const routineRef = doc(firestore, 'routines', foundRoutine.id);
      const routineDoc = await getDoc(routineRef);

      if (!routineDoc.exists()) {
        throw new Error('La rutina ya no existe');
      }

      const routineData = routineDoc.data();

      // Check if user is already authorized
      if (routineData.authorizedUsers && routineData.authorizedUsers.includes(userId)) {
        Alert.alert('Información', 'Ya tienes acceso a esta rutina');
        navigation.goBack();
        return;
      }

      // Add user to authorized users
      const authorizedUsers = routineData.authorizedUsers || [];
      authorizedUsers.push(userId);

      await updateDoc(routineRef, {
        authorizedUsers
      });

      Alert.alert(
        'Éxito',
        'Te has unido a la rutina correctamente',
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack()
          }
        ]
      );
    } catch (error) {
      console.error('Error al unirse a la rutina:', error);
      Alert.alert('Error', 'No se pudo unir a la rutina');
    } finally {
      setJoinLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView style={styles.scrollView}>
        <View style={styles.container}>
          <Card style={styles.card}>
            <Text style={styles.sectionTitle}>Buscar por Código</Text>
            <Text style={styles.sectionDescription}>
              Ingresa el código de rutina compartido que recibiste
            </Text>
            
            <View style={styles.searchContainer}>
              <Input
                value={routineCode}
                onChangeText={setRoutineCode}
                placeholder="Código de rutina"
                containerStyle={styles.searchInput}
                autoCapitalize="characters"
              />
              <Button
                title="Buscar"
                onPress={searchByCode}
                type="primary"
                loading={searchLoading}
                disabled={searchLoading || !routineCode.trim()}
                style={styles.searchButton}
              />
            </View>
          </Card>
          
          <Card style={styles.card}>
            <Text style={styles.sectionTitle}>Buscar por Usuario</Text>
            <Text style={styles.sectionDescription}>
              Ingresa el nombre de usuario de quien quieres ver sus rutinas compartidas
            </Text>
            
            <View style={styles.searchContainer}>
              <Input
                value={username}
                onChangeText={setUsername}
                placeholder="Nombre de usuario"
                containerStyle={styles.searchInput}
              />
              <Button
                title="Buscar"
                onPress={searchByUsername}
                type="primary"
                loading={searchLoading}
                disabled={searchLoading || !username.trim()}
                style={styles.searchButton}
              />
            </View>
          </Card>
          
          {foundRoutine && (
            <Card style={styles.routineCard}>
              <Text style={styles.routineTitle}>{foundRoutine.name}</Text>
              <Text style={styles.routineCreator}>Por: {foundRoutine.creatorName}</Text>
              <Text style={styles.routineDescription}>{foundRoutine.description}</Text>
              <Text style={styles.routineDays}>{foundRoutine.daysPerWeek} días por semana</Text>
              
              <Button
                title="Unirme a esta Rutina"
                onPress={joinRoutine}
                type="primary"
                loading={joinLoading}
                style={styles.joinButton}
              />
            </Card>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: colors.background,
  },
  container: {
    padding: spacing.lg,
  },
  card: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.grey.dark,
    marginBottom: spacing.md,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  searchInput: {
    flex: 1,
    marginRight: spacing.sm,
    marginBottom: 0,
  },
  searchButton: {
    marginTop: 20, // Align with input
  },
  routineCard: {
    marginBottom: spacing.lg,
    borderWidth: 2,
    borderColor: colors.primary,
  },
  routineTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  routineCreator: {
    fontSize: typography.fontSize.md,
    color: colors.primary,
    marginBottom: spacing.sm,
  },
  routineDescription: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    marginBottom: spacing.md,
  },
  routineDays: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    color: colors.grey.dark,
    marginBottom: spacing.md,
  },
  joinButton: {
    marginTop: spacing.sm,
  },
});

export default JoinRoutineScreen; 